import React, { useEffect, useState } from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { Paper, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import { ShowChartOutlined } from '@mui/icons-material'
import { muiOptions, MuiProps, defaultStyles, defaultColors } from '../../../infrastructure/materialUiThemeProvider'
import { t } from '../../../infrastructure/i18nextHelper'
import { StockChart } from '../chart/_stockChart'
import { Filters } from '../filters/_stockFilters'
import { StockBoardContainer } from '../stockBoardStore'
import { SimulationBoardContainer } from './simulationBoardStore'
import { SimulationTable } from './simulationTable'

function SimulationBoard({ classes }: MuiProps) {
    let store = StockBoardContainer.useContainer()
    let simulationStore = SimulationBoardContainer.useContainer()
    let [withStockChart, setWithStockChart] = useState<boolean>(true)

    useEffect(() => {
        let stockQuerySubscription = store.subscribeStockQuery()

        return () => {
            stockQuerySubscription.unsubscribe()
        }
    }, [])

    useEffect(() => {
        store.loadStocks(simulationStore.simulations)
    }, [simulationStore.simulations, store.stockMode])

    return (
        <div className={classes.pageContainer}
            style={{ gridTemplateAreas: withStockChart
                ? '"filters filters" "simulationTable stockChart"'
                : '"filters filters" "simulationTable simulationTable"' }}>
            <div className={classes.firstRow}>
                <div>
                    <div className={classes.titleContainer}>
                        <Typography className={classes.pageTitle} variant='h5' display='block' gutterBottom>
                            {t('stock.name')}
                        </Typography>
                    </div>
                    <Paper className={classes.displayToggles}>
                        <ToggleButtonGroup
                            value={withStockChart ? ['stockChart'] : []}
                            onChange={(_, newDisplayedBlocks: string[]) => setWithStockChart(newDisplayedBlocks.includes('stockChart'))}>
                            <Tooltip title={<Typography variant='body1'>{t('stock.label.displayStockChart')}</Typography>} placement='bottom'>
                                <ToggleButton value='stockChart' selected={withStockChart} classes={{ selected: classes.selected }}>
                                    <ShowChartOutlined />
                                </ToggleButton>
                            </Tooltip>
                        </ToggleButtonGroup>
                    </Paper>
                </div>
                <Filters isStockChartOrTableDisplayed={true} simulation={true} />
            </div>
            <div className={classes.simulationTable}>
                <SimulationTable />
            </div>
            <div className={classes.stockChart} style={{ display: withStockChart ? '' : 'none' }}>
                <StockChart />
            </div>
        </div>
    )
}

let styles = _ =>
    createStyles({
        titleContainer: {
            ...defaultStyles.flexRow,
        },
        firstRow: {
            'grid-area': 'filters',
            ...defaultStyles.flexRow,
            justifyContent: 'space-between',
            alignItems: 'start',
        },
        simulationTable: {
            'grid-area': 'simulationTable'
        },
        stockChart: {
            'grid-area': 'stockChart'
        },
        pageContainer: {
            display: 'grid',
            height: '100%',
            width: '100%',
            'grid-template-columns': 'minmax(0,1.8fr) minmax(0,2fr)',
            'grid-template-rows': '4em minmax(0, 1fr)',
            'grid-template-areas': '"filters filters" "simulationTable stockChart"',
            'grid-gap': '0.7em',
            overflow: 'hidden',
            'box-sizing': 'border-box',
            '&> div': {
                height: '100%',
                width: '100%',
                boxSizing: 'border-box'
            }
        },
        pageTitle: {
            color: defaultColors.grey.dark.color,
            margin: '0'
        },
        selected: {
            "&&": {
                backgroundColor: defaultColors.lightBlue.main.color,
                color: 'white',
                '&:hover': {
                    backgroundColor: defaultColors.lightBlue.light.color,
                },
            }
        },
        displayToggles: {
            height: '2em',
            display: 'flex',
            width: 'fit-content'
        }
    })

export default withStyles(styles, muiOptions)(SimulationBoard)
